'use client';

import { useMediaQuery } from '@mantine/hooks';
import { type ReactNode, createContext, useContext, useState } from 'react';

import { LARGE_SCREEN_WIDTH } from './constants';

interface SideNavContextValue {
  isExpanded: boolean;
  isLargeScreen: boolean;
  isHovered: boolean;
  setIsHovered: (hovered: boolean) => void;
  toggleExpanded: () => void;
  collapse: () => void;
}

const SideNavContext = createContext<SideNavContextValue | null>(null);

export function SideNavProvider({ children }: { children: ReactNode }) {
  const isLargeScreen = useMediaQuery(`(min-width: ${LARGE_SCREEN_WIDTH})`, true) ?? true;
  const [manualOverride, setManualOverride] = useState<boolean | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Use manual override if set, otherwise use media query default
  const isExpanded = manualOverride !== null ? manualOverride : isLargeScreen;

  const toggleExpanded = () => setManualOverride((prev) => (prev !== null ? !prev : !isLargeScreen));

  const collapse = () => setManualOverride(false);

  return (
    <SideNavContext.Provider
      value={{ isExpanded, isLargeScreen, isHovered, setIsHovered, toggleExpanded, collapse }}
    >
      {children}
    </SideNavContext.Provider>
  );
}

export function useSideNav() {
  const context = useContext(SideNavContext);
  if (!context) {
    throw new Error('useSideNav must be used within a SideNavProvider');
  }
  return context;
}
